import { batch, classNames, Component, h, signal, tag } from "omi";
import isEmpty from "lodash-es/isEmpty";

import { NWIcon } from "@/components";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

type Size = "xs" | "sm" | "md" | "lg";
type Props = {
  withIndicator?: boolean;
  withProBorder?: boolean;
  borderClass?: string;
  shape?: "circle" | "square";
  size?: Size;
  imgUrl?: string;
  interactive?: boolean;
};

@tag("nw-avatar")
export class NWAvatar extends Component<Props> {
  static css = [tailwind];
  private isLoading = signal(true);
  private hasError = signal(false);

  static propTypes = {
    withIndicator: Boolean,
    shape: String,
    size: String,
    borderClass: String,
    imgUrl: String,
    interactive: Boolean,
  };

  receiveProps(props: Props, oldProps: Props) {
    if (props.imgUrl != oldProps.imgUrl) {
      batch(() => {
        this.hasError.value = false;
        this.isLoading.value = true;
      });
    } else {
      return false;
    }
  }

  private appendSizeQueryString(url: string, size: Size): string {
    if (isEmpty(url)) return url;

    let mappedSize = "xs";
    switch (size) {
      case "sm":
        mappedSize = "xs";
        break;
      case "md":
        mappedSize = "sm";
        break;
      case "lg":
        mappedSize = "md";
        break;
    }

    return `${url}?size=${mappedSize}`;
  }

  render({
    imgUrl,
    borderClass,
    interactive = true,
    withProBorder = false,
    withIndicator = true,
    shape = "circle",
    size = "md",
  }: Props) {
    const avatarUrl = imgUrl ?? Store.user.info.value?.picture;

    return (
      <div
        className={classNames(
          "relative flex-shrink-0 rounded-full bg-secondary flex items-center justify-center",
          { "cursor-pointer hover:brightness-95": interactive },
          { "rounded-full border": shape === "circle" },
          { "rounded-lg": shape === "square" },
          { "h-6 w-6": size === "xs", "h-8 w-8": size === "sm", "h-9 w-9": size === "md", "h-12 w-12": size === "lg" },
          {
            "border-2 border-brand-400": withProBorder && Store.user.isPro(),
            border: withProBorder && !Store.user.isPro(),
          },
          borderClass,
        )}
      >
        {!this.hasError.value && !isEmpty(avatarUrl) ? (
          <div
            className={classNames("w-full h-full relative", {
              "rounded-full": shape === "circle",
              "rounded-md transition-all": shape === "square",
              "bg-gray-300 animate-pulse": this.isLoading.value,
            })}
          >
            <img
              src={this.appendSizeQueryString(avatarUrl!, size)}
              alt="Profile image"
              onError={() => {
                batch(() => {
                  this.hasError.value = true;
                  this.isLoading.value = false;
                });
              }}
              onLoad={() => {
                batch(() => {
                  this.isLoading.value = false;
                  this.hasError.value = false;
                });
              }}
              draggable={false}
              className={classNames(
                "w-full h-full object-cover pointer-events-none select-none",
                { "rounded-full": shape === "circle", "rounded-md": shape === "square" },
                { "opacity-0": this.isLoading.value },
              )}
            />
          </div>
        ) : (
          <NWIcon
            name="user"
            width={size === "sm" ? 16 : 20}
            height={size === "sm" ? 16 : 20}
            class="pointer-events-none select-none"
          />
        )}
        {withIndicator && (
          <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
        )}
      </div>
    );
  }
}
